/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import { Timestamp } from 'firebase/firestore';
import * as Yup from 'yup';
import { PPartStatus } from '../../../constants/index.js';

export const partSchema = Yup.object().shape({
  partId: Yup.string().required('Part ID is required'),
  revision: Yup.string()
    .matches(/^[A-Z]$/, 'Revision must be a single uppercase letter')
    .required('Revision is required'),
  name: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
  uid: Yup.string().required('UID is required'),
  metadata: Yup.object().default({}),
  metadataArr: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.string().required('Key is required')
          .test('unique-key', 'Key must be unique', (value, context) => {
            if (!value) return true;
            const { options } = context;
            const normalize = (str = '') => str.trim().toLowerCase().replace(/\s+/g, '');

            const metadataArr = options.context?.metadataArr || [];

            const currentKey = normalize(value);
            const matchCount = metadataArr
              .filter((item) => item.key && normalize(item.key) === currentKey).length;

            return matchCount <= 1;
          }),
        value: Yup.string().required('Value is required'),
      }),
    ),
  notes: Yup.string().default(''),
  /**
   * Reusable parts (tools, pistons, fixtures) needed to make this part, as
   * { projectDocId, pPartDocId } rows so a link can cross projects — pistons
   * live in a shared project. (Pre-row docs held bare same-project doc id
   * strings; scripts/dbMigration/migrateLinkedPartsToRows.js converted them.)
   */
  linkedParts: Yup.array().of(Yup.object().shape({
    projectDocId: Yup.string().required('Linked part projectDocId is required'),
    pPartDocId: Yup.string().required('Linked part pPartDocId is required'),
  })).test(
    'unique-linked-parts',
    'Each part can only be linked once',
    (linkedParts) => {
      if (!linkedParts?.length) return true;
      const keys = linkedParts
        .filter((row) => row?.projectDocId && row?.pPartDocId)
        .map((row) => `${row.projectDocId}/${row.pPartDocId}`);
      return new Set(keys).size === keys.length;
    },
  ).default([]),
  /**
   * Bill of materials: the component parts (possibly from other projects)
   * consumed onto one unit of this part, with per-unit quantities. Drives
   * the lot install checklists. Separate from linkedParts (reusable
   * tooling) and from flow edges (step order).
   */
  components: Yup.array().of(Yup.object().shape({
    projectDocId: Yup.string().required('Component projectDocId is required'),
    pPartDocId: Yup.string().required('Component pPartDocId is required'),
    quantity: Yup.number()
      .integer('Component quantity must be a whole number')
      .positive('Component quantity must be positive')
      .required('Component quantity is required'),
  })).test(
    'unique-component-lines',
    'Each component part can only appear once — merge duplicate lines',
    (components) => {
      if (!components?.length) return true;
      // Only complete rows count — blank editor rows should surface the
      // per-field required errors, not a bogus duplicate-line error.
      const keys = components
        .filter((c) => c?.projectDocId && c?.pPartDocId)
        .map((c) => `${c.projectDocId}/${c.pPartDocId}`);
      return new Set(keys).size === keys.length;
    },
  ).default([]),
  status: Yup.string().oneOf(
    Object.values(PPartStatus).map((status) => status),
  ).default(PPartStatus.ACTIVE),
});

/**
 * @typedef {Yup.InferType<typeof partSchema>} PartSchema
 * @typedef {BaseDocData & PartSchema & { createdAt: Timestamp; updatedAt: Timestamp; }} PartDocData
*/

/**
 * Class representing a part.
 * @class
 */
export default class Part {
  /** @type {PartDocData['validation']} */ validation = 'passed';
  /** Firestore document ID  @type {PartDocData['id']} */ id;
  /** @type {PartDocData['partId']} */ partId;
  /** @type {PartDocData['revision']} */ revision;
  /** @type {PartDocData['name']} */ name;
  /** @type {PartDocData['type']} */ type;
  /** @type {PartDocData['metadata']} */ metadata;
  /** @type {PartDocData['metadataArr']} */ metadataArr;
  /** @type {PartDocData['linkedParts']} */ linkedParts;
  /** @type {PartDocData['components']} */ components;
  /** @type {PartDocData['notes']} */ notes;
  /** @type {PartDocData['uid']} */ uid;
  /** @type {PartDocData['createdAt']} */ createdAt;
  /** @type {PartDocData['status']} */ status;

  /**
   * Create a part.
   * @param {any} param0 - Raw Firestore document.
   * @throws {Yup.ValidationError} - Raw Firestore document failed Yup validation.
   */
  constructor({
    id, createdAt, partId, revision, name, type, metadata = {}, metadataArr = [],
    notes = '', uid, status, linkedParts, components,
  }) {
    // Validate required fields
    const validatedData = partSchema.validateSync({
      partId,
      revision,
      name,
      type,
      metadata,
      metadataArr: metadataArr.length > 0
        ? metadataArr
        : Object.entries(metadata || {}).map(([key, value]) => ({ key, value })),
      notes,
      uid,
      status,
      linkedParts,
      components,
    });

    this.id = id || null;
    this.partId = validatedData.partId;
    this.revision = validatedData.revision;
    this.name = validatedData.name;
    this.type = validatedData.type;
    this.metadata = validatedData.metadata;
    this.metadataArr = validatedData.metadataArr;
    this.notes = validatedData.notes;
    this.linkedParts = validatedData.linkedParts || [];
    this.components = validatedData.components || [];
    this.uid = validatedData.uid;
    this.createdAt = createdAt;
    this.status = validatedData.status;
  }

  /**
   * Converts the {@link Part} instance to a Firestore data object.
   * @returns {Omit<PartDocData, 'id'>} {@link PartDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    return {
      partId: this.partId,
      revision: this.revision,
      name: this.name,
      type: this.type,
      notes: this.notes,
      metadata: {
        ...(this.metadataArr.reduce(
          (acc, { key, value }) => ({ ...acc, [key]: value }),
          {},
        )),
        // Rebuilt as plain objects — Firestore rejects Yup's cast objects.
        linkedParts: this.linkedParts.map((row) => ({
          projectDocId: row.projectDocId,
          pPartDocId: row.pPartDocId,
        })),
        components: this.components.map((component) => ({
          projectDocId: component.projectDocId,
          pPartDocId: component.pPartDocId,
          quantity: component.quantity,
        })),
      },
      createdAt: this.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      uid: this.uid,
      status: this.status,
    };
  }

  /**
   * Creates a {@link Part} instance from Firestore data, lifting the
   * base metadata-stored fields (linkedParts, components) out of metadata.
   * @param {any} docData - Raw Firestore document.
   * @returns {Part} A {@link Part} instance.
   * @throws {Yup.ValidationError} Raw Firestore document failed Yup validation.
   */
  static fromFirestore(docData) {
    const { linkedParts, components, ...restMetadata } = docData.metadata || {};
    return new Part({
      ...docData,
      linkedParts,
      components,
      metadata: restMetadata || {},
    });
  }
}
